import { PageContainer } from '@/components/page/PageContainer';
import { Button } from '@/components/shared/button/Button';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { strings } from '@/services/translation/strings';
import { useSelectedDate } from '@/services/useSelectedDate';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { FiCalendar, FiEdit2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { NavPage } from '../../components/page/Breadcrumbs';
import { PageChangeDirection, PageHeader } from '../../components/page/PageHeader';
import { usePublishWorkingRoster } from './hooks/usePublishWorkingRoster';
import { useSaveRoster } from './hooks/useSaveRoster';
import { useUnpublishWorkingRoster } from './hooks/useUnpublishWorkingRoster';
import { RosterProvider } from './providers/RosterService';
import { useWorkingRoster } from './providers/WorkingRosterService';
import { Roster } from './Roster';

const RosterPageInner: FC<{ date: Date }> = ({ date }) => {
  const startOfMonth = new Date(date);
  startOfMonth.setUTCDate(1);

  const breadcrumbs: NavPage[] = [
    {
      name: 'Rosters',
      to: `/rosters/calendar/${dateToApiFormat(startOfMonth)}`
    },
    {
      name: format(date, 'yyyy/MM/dd'),
      to: '/rosters/' + dateToApiFormat(date)
    }
  ];

  const { saveRoster, fetching: fetchingSave } = useSaveRoster();
  const { publishRoster, fetching: fetchingPublish } = usePublishWorkingRoster();
  const { unpublishRoster, fetching: fetchingUnpublish } = useUnpublishWorkingRoster();

  const { workingRoster } = useWorkingRoster();
  const published = workingRoster?.published;

  const saveNeeded = useConfirm(s => s.confirmationNeeded);

  const [isUnpublishModalOpen, setIsUnpublishModalOpen] = useState(false);

  const handleConfirmUnpublish = async () => {
    await unpublishRoster();
    setIsUnpublishModalOpen(false);
  };

  const navigate = useNavigate();
  const onChangePage = (direction: PageChangeDirection) => {
    const newDate = new Date(date);

    if (direction === PageChangeDirection.PREV) {
      newDate.setUTCDate(newDate.getUTCDate() - 1);
    } else if (direction === PageChangeDirection.NEXT) {
      newDate.setUTCDate(newDate.getUTCDate() + 1);
    }

    navigate(`/rosters/${dateToApiFormat(newDate)}`);
  };

  // Fully locked (e.g. a past roster) vs. locked-but-editable-via-unpublish (published).
  // See note below re: workingRoster.readOnly.
  const fullyLocked = workingRoster?.readOnly && !published;

  return (
    <PageContainer
      lockHeader
      fullWidth
      customHeader={
        <PageHeader
          fullWidth
          centeredTitle
          pageTitle={format(date, 'EEEE do MMMM yyyy')}
          icon={<FiCalendar />}
          breadcrumbs={breadcrumbs}
          onChangePage={onChangePage}
        >
          {workingRoster && !fullyLocked && (
            <div className="flex gap-3 items-center">
              {!published && (
                <Button primary disabled={!saveNeeded} loading={fetchingSave} onClick={saveRoster}>
                  {strings.common.save}
                </Button>
              )}
              <Button
                primary
                disabled={saveNeeded || fetchingSave}
                loading={published ? fetchingUnpublish : fetchingPublish}
                onClick={published ? () => setIsUnpublishModalOpen(true) : publishRoster}
                className="flex gap-2 items-center"
              >
                {published && <FiEdit2 className="h-6 w-6" />}
                {published ? strings.daily.roster.editRoster : strings.daily.roster.publish}
              </Button>
            </div>
          )}
        </PageHeader>
      }
    >
      <div className="h-full flex flex-col flex-1">
        <Roster />
      </div>

      <ConfirmDialog
        onConfirm={handleConfirmUnpublish}
        open={isUnpublishModalOpen}
        onClose={() => setIsUnpublishModalOpen(false)}
        title={strings.daily.roster.unpublishConfirmTitle}
        confirmLabel={strings.daily.roster.unpublishConfirmButton}
        cancelLabel={strings.common.cancel}
      >
        {strings.daily.roster.unpublishConfirmBody}
      </ConfirmDialog>
    </PageContainer>
  );
};

export const RosterPage: FC = () => {
  const date = useSelectedDate();

  return (
    <RosterProvider date={date}>
      <RosterPageInner date={date} />
    </RosterProvider>
  );
};
